import { useEffect, useState } from 'react';
import { ArrowRight, CreditCard, Loader2, QrCode } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { billingApi, type CheckoutMethod, type CheckoutResponse } from '@/services/api';
import type { Lang } from '@/utils/i18n';
import { paymentCopy } from './payment-copy';
import { Button } from '@/components/ui/button';
import { cn } from '@/utils/functions/cn';

export function ActivationCheckout({ mac, packageId, language, canPay, price, currency, onBusy }: {
  mac: string; packageId: number | null; language: Lang; canPay: boolean; price?: string;
  /** Currency charged for this language; PIX exists only for BRL. */
  currency?: string;
  onBusy: (busy: boolean) => void;
}) {
  const c = paymentCopy[language];
  const navigate = useNavigate();
  const [methods, setMethods] = useState<CheckoutMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [method, setMethod] = useState<CheckoutMethod>('credit_card');
  const [paying, setPaying] = useState(false);
  const [checkout, setCheckout] = useState<CheckoutResponse | null>(null);
  const [error, setError] = useState('');
  const [terminal, setTerminal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    billingApi.getMethods().then((items) => {
      if (!active) return;
      const available = items.map((m) => m.method)
        .filter((m) => m === 'pix' || m === 'credit_card')
        .filter((m) => m !== 'pix' || !currency || currency.toUpperCase() === 'BRL');
      setMethods(available);
      setMethod((current) => available.includes(current) ? current : available[0]);
    }).catch(() => { if (active) setMethods([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [reload, currency]);

  // Restore the checkout after a reload rather than creating another charge.
  useEffect(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem('moova_checkout') || 'null');
      if (saved?.mac === mac && saved?.packageId === packageId && saved?.payment?.transactionId) {
        setCheckout(saved.payment);
      }
    } catch { /* no saved checkout */ }
  }, [mac, packageId]);

  useEffect(() => { onBusy(paying || !!checkout && !terminal); }, [paying, checkout, terminal, onBusy]);

  useEffect(() => {
    if (!checkout || terminal) return;
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const status = await billingApi.getCheckoutStatus(checkout.transactionId);
        if (!active) return;
        setError('');
        if (status.paymentUrl && !checkout.paymentUrl) {
          setCheckout((current) => current ? { ...current, paymentUrl: status.paymentUrl } : current);
        }
        if (status.status === 'COMPLETED') {
          navigate(`/activation/success?mac=${encodeURIComponent(mac)}&txn=${encodeURIComponent(checkout.transactionId)}`);
          return;
        }
        if (['FAILED', 'CANCELLED', 'REFUNDED'].includes(status.status)) {
          setTerminal(true);
          sessionStorage.removeItem('moova_checkout');
          return;
        }
      } catch { if (active) setError(c.network); }
      if (active) timer = setTimeout(poll, 3500);
    };
    void poll();
    return () => { active = false; clearTimeout(timer); };
  }, [checkout, terminal, c.network, navigate, mac]);

  async function pay() {
    if (!canPay || !packageId || paying || checkout) return;
    setPaying(true); setError('');
    try {
      const payment = await billingApi.createActivationPayment({
        mac, packageId, lang: language, paymentMethod: method,
        successUrl: `${location.origin}/activation/success?mac=${encodeURIComponent(mac)}&txn={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${location.origin}/activation?mac=${encodeURIComponent(mac)}`,
      });
      sessionStorage.setItem('moova_txn', payment.transactionId);
      sessionStorage.setItem('moova_checkout', JSON.stringify({ mac, packageId, payment }));
      setCheckout(payment);
      if (payment.status === 'COMPLETED') navigate(`/activation/success?mac=${encodeURIComponent(mac)}&txn=${encodeURIComponent(payment.transactionId)}`);
      else if (!payment.pix && payment.paymentUrl) location.assign(payment.paymentUrl);
    } catch (e) { setError((e as Error).message); }
    finally { setPaying(false); }
  }

  if (checkout) return (
    <div className="space-y-3 rounded-[14px] border border-brand-accent/25 bg-brand-accent/[0.06] p-4" aria-live="polite">
      <p className="flex items-center gap-2 text-sm font-medium">{!terminal && <Loader2 className="h-4 w-4 animate-spin" />}{terminal ? c.failed : c.waiting}</p>
      {terminal ? <>
        <p className="text-xs text-white/60">{c.failedDetail}</p>
        <Button onClick={() => { setCheckout(null); setTerminal(false); }}>{c.newPayment}</Button>
      </> : <>
        {checkout.pix?.qrCodeBase64 && <img className="mx-auto w-48 max-w-full rounded-lg bg-white p-2" src={checkout.pix.qrCodeBase64} alt="PIX QR code" />}
        {checkout.pix?.copyPaste && <>
          <textarea aria-label={c.copy} readOnly value={checkout.pix.copyPaste} className="h-16 w-full resize-none rounded-lg border border-white/10 bg-black/20 p-2 font-mono text-xs" />
          <Button className="w-full" onClick={async () => {
            try { await navigator.clipboard.writeText(checkout.pix!.copyPaste!); setCopied(true); }
            catch { setCopied(false); }
          }}>{copied ? c.copied : c.copy}</Button>
        </>}
        {checkout.pix?.expiresAt && <p className="text-center text-xs text-white/50">{c.expires} {new Date(checkout.pix.expiresAt).toLocaleTimeString(language)}</p>}
        {checkout.paymentUrl && <a className="block text-center text-xs text-brand-sky underline" href={checkout.paymentUrl}>{c.hosted}</a>}
      </>}
      {error && <p role="alert" className="text-xs text-amber-300">{error}</p>}
    </div>
  );

  return <div className="space-y-3">
    {loading ? <p className="text-xs text-white/50">{c.loading}</p> : !methods.length ? <div role="alert" className="space-y-2 text-xs text-amber-300">
      <p>{c.unavailable}</p><button type="button" onClick={() => setReload((n) => n + 1)} className="underline">{c.retry}</button>
    </div> : <fieldset>
      <legend className="mb-2 text-[11px] font-medium uppercase tracking-[0.12em] text-white/50">{c.method}</legend>
      <div className="flex gap-2">{methods.map((value) => {
        const Icon = value === 'pix' ? QrCode : CreditCard;
        return <label key={value} className={cn('flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-sm', method === value ? 'border-brand-accent bg-brand-accent/10' : 'border-white/10')}>
          <input className="sr-only peer" type="radio" name="paymentMethod" value={value} checked={method === value} onChange={() => setMethod(value)} disabled={paying} />
          <Icon className="h-4 w-4 peer-focus-visible:outline peer-focus-visible:outline-2" />{value === 'pix' ? c.pix : c.card}
        </label>;
      })}</div>
    </fieldset>}
    {error && <p role="alert" className="rounded-xl border border-red-500/20 bg-red-500/10 p-3 text-xs text-red-400">{error}</p>}
    <Button disabled={!canPay || loading || !methods.length || paying} onClick={pay} className="h-12 w-full rounded-[14px] bg-brand-accent text-white hover:bg-brand-accent/90 disabled:bg-white/[0.06] disabled:text-white/35">
      {paying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{c.pay}{price ? ` — ${price}` : ''}<ArrowRight className="ml-2 h-4 w-4" />
    </Button>
  </div>;
}
